import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { confirmToast } from '../../utils/confirmToast';

interface UserBrand {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  websiteUrl: string;
  message: string;
  createdAt: string;
  updatedAt: string;
  status: string;
}


const UserBrandsSection: React.FC = () => {
  const [brands, setBrands] = useState<UserBrand[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || '';
    
    // Fetch brands data
    fetch(`${API_BASE_URL}/api/userbrands`)
      .then(res => res.json())
      .then(brandsData => {
        setBrands(brandsData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching brands data:', error);
        setBrands([]);
        setLoading(false);
      });
  }, []);

  const updateStatus = async (id: string, newStatus: string) => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || '';
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/userbrands/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        setBrands(prev => prev.map(brand => 
          brand._id === id ? { ...brand, status: newStatus } : brand
        ));
      } else {
        const errorData = await response.json();
        console.error('Failed to update status:', errorData);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteBrand = async (id: string) => {
    const confirmed = await confirmToast('Are you sure you want to delete this brand?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    const API_BASE_URL = import.meta.env.VITE_API_URL || '';
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/userbrands/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setBrands(prev => prev.filter(brand => brand._id !== id));
        toast.success('Brand deleted');
      } else {
        const errorData = await response.json();
        console.error('Failed to delete brand:', errorData);
        toast.error('Failed to delete brand. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error('An error occurred while deleting the brand.');
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status.toLowerCase()) {
      case 'approved':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'rejected':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen w-full overflow-x-hidden">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="w-full sm:w-auto">
            <h1 className="text-2xl font-bold text-gray-900">User Brands</h1>
            <p className="text-gray-600 mt-1">Manage brand user accounts and applications</p>
          </div>
          <div className="text-left sm:text-center">
            <span className="text-sm text-gray-500">Total Brands:</span>
            <span className="ml-2 text-lg font-semibold text-blue-600">{brands.length}</span>
          </div>
        </div>
      </div>

      <div className="w-full px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          {brands.map((brand) => (
            <div key={brand._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-full">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-start space-x-3 w-full sm:w-auto">
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                    🏢
                  </div>
                  
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                      <h3 className="text-sm font-medium text-gray-900 break-words">{brand.name}</h3>
                      <span className={`${getStatusBadge(brand.status)} mt-1 sm:mt-0`}>
                        {brand.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 break-words">{brand.email}</p>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-500">Phone: {brand.phoneNumber}</p>
                      <p className="text-xs text-gray-500 break-words">
                        Website: 
                        <a 
                          href={brand.websiteUrl.startsWith('http') ? brand.websiteUrl : `https://${brand.websiteUrl}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 hover:underline ml-1 break-all"
                        >
                          {brand.websiteUrl}
                        </a>
                      </p>
                      <p className="text-xs text-gray-500 break-words">
                        Message: <span className="text-gray-700">{brand.message || 'No message'}</span>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-2 sm:mt-0">
                  <div className="text-xs text-gray-400 whitespace-nowrap">
                    {formatDate(brand.createdAt)}
                  </div>
                  <div className="relative w-full sm:w-auto">
                    <select
                      value={brand.status}
                      onChange={(e) => updateStatus(brand._id, e.target.value)}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <button 
                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                    onClick={() => deleteBrand(brand._id)}
                    title="Delete brand"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserBrandsSection;
